export type BrowserNotificationPermissionStatus =
  | 'unsupported'
  | 'not_enabled'
  | 'enabled'
  | 'blocked';

export type SerializedPushSubscription = {
  endpoint: string;
  expirationTime: number | null;
  keys: { p256dh: string; auth: string };
};

export const BROWSER_NOTIFICATION_OPT_OUT_KEY =
  'imai:browser-notifications:explicitly-disabled';
let browserNotificationOptOutFallback = false;

export function hasBrowserNotificationOptOut(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.localStorage.getItem(BROWSER_NOTIFICATION_OPT_OUT_KEY) === '1'
    );
  } catch {
    return browserNotificationOptOutFallback;
  }
}

export function setBrowserNotificationOptOut(optedOut: boolean): void {
  if (typeof window === 'undefined') return;
  browserNotificationOptOutFallback = optedOut;
  try {
    if (optedOut) {
      window.localStorage.setItem(BROWSER_NOTIFICATION_OPT_OUT_KEY, '1');
      return;
    }
    window.localStorage.removeItem(BROWSER_NOTIFICATION_OPT_OUT_KEY);
  } catch {
    // Browser storage can be unavailable in restricted privacy modes.
  }
}

export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const decoded = atob(base64);
  const bytes = new Uint8Array(decoded.length);
  for (let index = 0; index < decoded.length; index += 1) {
    bytes[index] = decoded.charCodeAt(index);
  }
  return bytes;
}

export function serializePushSubscription(
  subscription: Pick<PushSubscription, 'endpoint' | 'expirationTime' | 'toJSON'>
): SerializedPushSubscription {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) throw new Error('PUSH_SUBSCRIPTION_KEYS_MISSING');
  return {
    endpoint: subscription.endpoint,
    expirationTime: subscription.expirationTime,
    keys: { p256dh, auth },
  };
}

export function resolveBrowserNotificationStatus(input: {
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
}): BrowserNotificationPermissionStatus {
  if (!input.supported) return 'unsupported';
  if (input.permission === 'denied') return 'blocked';
  if (input.permission === 'granted' && input.subscribed) return 'enabled';
  return 'not_enabled';
}

export function shouldReconcileBrowserNotifications(input: {
  configured: boolean;
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  optedOut?: boolean;
}): boolean {
  return (
    input.configured &&
    input.supported &&
    !input.optedOut &&
    input.permission === 'granted' &&
    !input.subscribed
  );
}

export function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export function isStandaloneIosWebApp(): boolean {
  if (typeof window === 'undefined') return false;
  const navigatorWithStandalone = navigator as Navigator & {
    standalone?: boolean;
  };
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return (
    !isIos ||
    navigatorWithStandalone.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

export async function registerImaiPushServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register('/imai-push-service-worker.js', {
    scope: '/',
  });
}

let workerUpdateRequested = false;

/**
 * Asks the browser to fetch the push worker once per page load, so a fixed
 * worker replaces an installed one without waiting for the browser's own
 * update check. Resolves false when nothing is registered or it failed.
 */
export async function updateImaiPushServiceWorker(): Promise<boolean> {
  if (workerUpdateRequested || !isWebPushSupported()) return false;
  workerUpdateRequested = true;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) return false;
    await registration.update();
    return true;
  } catch {
    return false;
  }
}

export async function ensureBrowserNotificationsEnabled(input: {
  publicKey: string | null;
  reason: 'task_follow' | 'thread_all_updates' | 'settings';
  registerSubscription(
    subscription: SerializedPushSubscription,
    deviceLabel: string
  ): Promise<void>;
}): Promise<BrowserNotificationPermissionStatus> {
  if (!isWebPushSupported() || !input.publicKey) return 'unsupported';
  if (!isStandaloneIosWebApp()) return 'not_enabled';
  let permission = Notification.permission;
  if (permission === 'denied') return 'blocked';
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return permission === 'denied' ? 'blocked' : 'not_enabled';
  }
  let subscription: PushSubscription | null = null;
  let createdSubscription = false;
  try {
    const registration = await registerImaiPushServiceWorker();
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      subscription = existing;
    } else {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(input.publicKey),
      });
      createdSubscription = true;
    }
    await input.registerSubscription(
      serializePushSubscription(subscription),
      navigator.userAgent.slice(0, 60)
    );
    return 'enabled';
  } catch {
    // A local subscription is not usable until the server accepts it. Remove
    // the orphan so later browser-only snapshots cannot report a false
    // "enabled" state after registration failed.
    if (createdSubscription) {
      await subscription?.unsubscribe().catch(() => false);
    }
    return 'not_enabled';
  }
}

/** The browser's current push subscription, if any (no prompt, no create). */
async function currentLocalPushSubscription(): Promise<PushSubscription | null> {
  if (!isWebPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  return (await registration?.pushManager.getSubscription()) ?? null;
}

/** Closes notifications this site already showed (an account's private text). */
async function closeShownNotifications(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration('/');
  const shown = (await registration?.getNotifications()) ?? [];
  for (const notification of shown) notification.close();
}

/**
 * Sign-out: this browser stops receiving the account's pushes even when the
 * server can't be reached. The local subscription goes first (so nothing
 * more arrives), then shown notifications close, then the server row is
 * revoked best-effort; a row left behind fails with 410 on its next push and
 * is pruned, and the sender also drops rows whose sign-in ended.
 */
export async function detachLocalPushSubscription(input: {
  revokeEndpoint(endpoint: string): Promise<void>;
}): Promise<boolean> {
  const subscription = await currentLocalPushSubscription();
  await closeShownNotifications().catch(() => undefined);
  if (!subscription) return false;
  const { endpoint } = subscription;
  await subscription.unsubscribe().catch(() => false);
  await input.revokeEndpoint(endpoint).catch(() => undefined);
  return true;
}

/**
 * A signed-out page in a browser that still holds a push subscription (the
 * session ended without the sign-out page, e.g. cookies cleared): drop it
 * locally. No server call is possible without a session; the row is pruned
 * by the sender (410, or its sign-in has ended).
 */
export async function releaseLocalPushSubscription(): Promise<boolean> {
  const subscription = await currentLocalPushSubscription();
  await closeShownNotifications().catch(() => undefined);
  if (!subscription) return false;
  return subscription.unsubscribe().catch(() => false);
}

export type EndpointOwnershipAction = 'keep' | 'adopt' | 'release';

/**
 * On sign-in, for a browser that already holds a push subscription:
 * - this account already owns it → keep;
 * - another account (or no one) owns it, and this browser allows push and
 *   wasn't turned off here → adopt it for this account, so a user who had
 *   push on is never left without it;
 * - otherwise → release it, so no earlier account keeps getting pushes.
 */
export function endpointOwnershipAction(input: {
  ownedByCurrentUser: boolean;
  permission: NotificationPermission;
  optedOut: boolean;
}): EndpointOwnershipAction {
  if (input.ownedByCurrentUser) return 'keep';
  if (input.permission === 'granted' && !input.optedOut) return 'adopt';
  return 'release';
}

/** The local subscription to reconcile on sign-in, or null. */
export async function localPushSubscriptionForOwnership(): Promise<PushSubscription | null> {
  return currentLocalPushSubscription();
}
